import React from 'react';
import classNames from 'classnames';

import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import { describeClassPoints } from '../Seating/seatingLayout';
import { PassengerCounts } from './tripClassFares';
import { perHeadLabel } from './fareLabels';

/**
 * What a class includes, on demand.
 *
 * <p><b>Behind an icon rather than on the card.</b> The full list is four or five lines per class,
 * and three of those stacked under a row of prices stops being a comparison — the table is for
 * deciding between classes, and the detail is for the one booker in ten who wants to know why
 * Premium costs more. So the card carries the one-line description and this carries the rest.</p>
 *
 * <p>Built on the app's own <c>Popover</c> in <c>floating</c> mode, which portals the panel out to
 * the style root and pins it to the trigger with viewport coordinates. That is not decoration: the
 * fare table sets <c>overflow: hidden</c> to keep its corners rounded, and an absolutely-positioned
 * panel inside it is cut off at the bottom of the list. Floating also brings the flip-above
 * behaviour, so a class near the foot of the page opens upwards rather than off-screen.</p>
 */
interface Props {
	classPrice: TripClassPrice;
	counts: PassengerCounts;
	/** Drawn on the class's own colour when open, so the panel is tied to the class it describes. */
	colour: string;
}

export default function ClassInfoPopover({ classPrice, counts, colour }: Props) {
	const [open, setOpen] = React.useState(false);
	const lines = (classPrice.attributes ?? []).filter(x => x.showInMoreInfo);
	// The description as points, so the dashes an operator types to list them are not printed.
	const description = describeClassPoints(classPrice.description);

	// Nothing to say beyond what the card already says.
	if (lines.length === 0 && description.length === 0) {
		return null;
	}

	return (
		<Popover
			floating
			containerClassName="class-info"
			buttonClassName={classNames('class-info__trigger', { 'class-info__trigger--on': open })}
			popoverClassName="class-info__panel"
			onAfterClose={() => setOpen(false)}
			buttonContent={
				<span
					className="class-info__mark"
					style={open ? { background: colour, borderColor: colour } : undefined}
					aria-label={`What ${classPrice.name} includes`}
					// The trigger is the Popover's own button; this only toggles the colour that
					// tells a booker which class they are reading about.
					onClick={() => setOpen(value => !value)}
				>
					i
				</span>
			}
			popoverContent={() => (
				<>
					<span className="class-info__head">
						<b>{classPrice.name}</b>
						<em>{perHeadLabel(classPrice, counts)}</em>
					</span>

					{description.length === 1 && <span className="class-info__desc">{description[0]}</span>}
					{description.length > 1 && (
						<span className="class-info__desc">
							{description.map(point => (
								<span key={point} className="block">
									{`\u2022 ${point}`}
								</span>
							))}
						</span>
					)}

					{lines.length > 0 && (
						<span className="class-info__lines">
							{lines.map(line => (
								<span key={line.label} className="class-info__line">
									<span className="class-info__bullet">
										{/*
											The attribute's own icon where it has one. A tick is the
											fallback rather than the rule: "no lounge access" with a
											tick beside it says the opposite of what it means, and
											the icon is how an operator avoids that.
										*/}
										{line.iconName ? (
											<Icon name={line.iconName} useMaterialIcons />
										) : (
											<Icon name="tick" />
										)}
									</span>
									{line.label}
								</span>
							))}
						</span>
					)}
				</>
			)}
		/>
	);
}
